import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Clock3 } from "lucide-react";
import API from "../services/api";
import { getMarketStatusLabel, getMarketWindowForNow, getSuggestedSession, isMarketClosed } from "../utils/marketStatus";

const fallbackMarkets = [
  { id: 1, name: "SRIDEVI NIGHT", open_time: "7:05 pm", close_time: "8:05 pm" },
  { id: 2, name: "MADHUR NIGHT", open_time: "8:20 pm", close_time: "10:20 pm" },
  { id: 3, name: "MILAN NIGHT", open_time: "8:50 pm", close_time: "10:50 pm" },
  { id: 4, name: "ANMOL BAZAR", open_time: "9:00 pm", close_time: "11:55 pm" },
  { id: 5, name: "KALYAN NIGHT", open_time: "9:25 pm", close_time: "11:25 pm" },
  { id: 6, name: "RAJDHANI NIGHT", open_time: "9:30 pm", close_time: "11:50 pm" },
  { id: 7, name: "MAIN BAZAR", open_time: "9:45 pm", close_time: "11:55 pm" },
  { id: 9, name: "KALYAN", open_time: "4:10 pm", close_time: "6:10 pm" },
  { id: 10, name: "RAJDHANI DAY", open_time: "2:00 pm", close_time: "5:00 pm" },
  { id: 11, name: "MILAN DAY", open_time: "2:45 pm", close_time: "4:45 pm" },
  { id: 14, name: "TIME BAZAR", open_time: "12:45 pm", close_time: "1:45 pm" },
  { id: 15, name: "SRIDEVI", open_time: "11:35 am", close_time: "12:35 pm" },
];

const gameTypes = [
  { title: "Single Digit",        icon: "1",    color: "#f97316", gradient: "135deg, #ea580c, #f97316", path: "single" },
  { title: "Single Digit Bulk",   icon: "1+",   color: "#059669", gradient: "135deg, #047857, #059669", path: "single-bulk" },
  { title: "Jodi Digit",          icon: "12",   color: "#7c3aed", gradient: "135deg, #6d28d9, #7c3aed", path: "jodi" },
  { title: "Jodi Digit Bulk",     icon: "12+",  color: "#0891b2", gradient: "135deg, #0e7490, #0891b2", path: "jodi-bulk" },
  { title: "Single Pana",         icon: "SP",   color: "#c026d3", gradient: "135deg, #a21caf, #c026d3", path: "single-patti" },
  { title: "Single Pana Bulk",    icon: "SP+",  color: "#0284c7", gradient: "135deg, #0369a1, #0284c7", path: "sp-bulk" },
  { title: "Double Pana",         icon: "DP",   color: "#16a34a", gradient: "135deg, #15803d, #16a34a", path: "double-patti" },
  { title: "Double Pana Bulk",    icon: "DP+",  color: "#0d9488", gradient: "135deg, #0f766e, #0d9488", path: "dp-bulk" },
  { title: "SP Motor",            icon: "SM",   color: "#dc2626", gradient: "135deg, #b91c1c, #dc2626", path: "sp-motor" },
  { title: "DP Motor",            icon: "DM",   color: "#d97706", gradient: "135deg, #b45309, #d97706", path: "dp-motor" },
  { title: "Red Bracket",         icon: "][",   color: "#9333ea", gradient: "135deg, #7e22ce, #9333ea", path: "red-bracket" },
  { title: "Triple Pana",         icon: "TP",   color: "#db2777", gradient: "135deg, #be185d, #db2777", path: "triple-patti" },
  { title: "Half Sangam",         icon: "HS",   color: "#0e7490", gradient: "135deg, #164e63, #0e7490", path: "half-sangam" },
  { title: "Full Sangam",         icon: "FS",   color: "#166534", gradient: "135deg, #14532d, #166534", path: "full-sangam" },
];

const closeSessionHiddenPaths = new Set(["jodi", "jodi-bulk", "red-bracket", "half-sangam", "full-sangam"]);

function Bet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const mainRef = useRef(null);
  const selectedMarketId = id || sessionStorage.getItem("selectedMarketId") || "1";

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (!user?.id) navigate("/login", { replace: true });
  }, [navigate]);

  useEffect(() => {
    API.get("/markets")
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        setMarkets(rows.filter((m) => (m.market_type || "normal") !== "starline"));
      })
      .catch(() => setMarkets([]));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (mainRef.current) mainRef.current.scrollTop = 0;
    const frame = window.requestAnimationFrame(() => {
      if (mainRef.current) mainRef.current.scrollTop = 0;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedMarketId]);

  const market = useMemo(() => {
    const all = markets.length ? markets : fallbackMarkets;
    return (
      all.find((m) => String(m.id) === String(selectedMarketId)) ||
      fallbackMarkets.find((m) => String(m.id) === String(selectedMarketId)) ||
      fallbackMarkets[0]
    );
  }, [selectedMarketId, markets]);

  const marketWindow = getMarketWindowForNow(market);
  const openTime = marketWindow.open_time || market.open_time || "";
  const closeTime = marketWindow.close_time || market.close_time || "";
  const marketClosed = isMarketClosed(market);
  const suggestedSession = getSuggestedSession(market);
  const visibleGameTypes = suggestedSession === "CLOSE"
    ? gameTypes.filter((g) => !closeSessionHiddenPaths.has(g.path))
    : gameTypes;

  const openGame = (game) => {
    if (marketClosed) { alert("Market closed for today"); return; }
    sessionStorage.setItem("selectedMarketId", String(market.id || selectedMarketId));
    navigate(`/${game.path}`);
  };

  return (
    <div style={page} ref={mainRef}>
      {/* Header */}
      <header style={header}>
        <button type="button" style={backBtn} onClick={() => navigate(-1)}>
          <ArrowLeft size={22} color="#fff" />
        </button>
        <div style={headerCenter}>
          <span style={headerTitle}>{market.name || "SELECT GAME"}</span>
          <span style={headerTime}>
            <Clock3 size={11} color="rgba(255,255,255,0.7)" />
            {" "}{formatTime(openTime)} - {formatTime(closeTime)}
          </span>
        </div>
        <span style={marketClosed ? closedBadge : openBadge}>
          {getMarketStatusLabel(market)}
        </span>
      </header>

      {/* Game grid */}
      <main style={gridWrap}>
        <div style={grid}>
          {visibleGameTypes.map((game) => (
            <button
              key={game.title}
              type="button"
              style={marketClosed ? tileDisabled : {
                ...tile,
                background: `linear-gradient(135deg, ${game.color}14 0%, ${game.color}08 100%)`,
                borderColor: `${game.color}30`,
              }}
              onClick={() => openGame(game)}
            >
              <div style={{ width: 38, height: 38, borderRadius: 11, background: marketClosed ? "#e5e7eb" : `linear-gradient(${game.gradient})`, display: "grid", placeItems: "center", flexShrink: 0, boxShadow: marketClosed ? "none" : `0 4px 10px ${game.color}40` }}>
                <span style={{ color: "#fff", fontSize: 11, fontWeight: 900, letterSpacing: "0.02em" }}>{game.icon}</span>
              </div>
              <span style={marketClosed ? tileNameDisabled : { ...tileName, color: "#1e293b" }}>
                {game.title}
              </span>
              <span style={{ color: marketClosed ? "#94a3b8" : game.color, fontSize: 18, fontWeight: 800, lineHeight: 1, flexShrink: 0 }}>›</span>
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}

const formatTime = (v) => {
  if (!v) return "--:--";
  const m = String(v).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return String(v);
  const h = Number(m[1]);
  return `${String(((h + 11) % 12) + 1).padStart(2, "0")}:${m[2]} ${h >= 12 ? "PM" : "AM"}`;
};

const page = {
  minHeight: "100vh",
  background: "#f1f5f9",
  fontFamily: "Inter, system-ui, sans-serif",
  color: "#111827",
};

const header = {
  position: "sticky",
  top: 0,
  zIndex: 40,
  background: "#0f172a",
  height: "60px",
  display: "flex",
  alignItems: "center",
  padding: "0 12px",
  gap: "10px",
  boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
};
const backBtn = {
  border: 0,
  background: "transparent",
  display: "grid",
  placeItems: "center",
  padding: "6px",
  cursor: "pointer",
  flexShrink: 0,
};
const headerCenter = {
  flex: 1,
  display: "grid",
  gap: "1px",
  minWidth: 0,
};
const headerTitle = {
  color: "#fff",
  fontSize: "15px",
  fontWeight: 900,
  textTransform: "uppercase",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const headerTime = {
  color: "rgba(255,255,255,0.65)",
  fontSize: "11px",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: "3px",
};
const openBadge = {
  padding: "3px 10px",
  borderRadius: "999px",
  background: "#dcfce7",
  color: "#15803d",
  fontSize: "11px",
  fontWeight: 800,
  flexShrink: 0,
};
const closedBadge = {
  ...openBadge,
  background: "#fee2e2",
  color: "#991b1b",
};

const gridWrap = {
  padding: "16px 12px 24px",
  maxWidth: "600px",
  margin: "0 auto",
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "9px",
};

const tile = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: "14px",
  padding: "11px 12px",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "11px",
  cursor: "pointer",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  WebkitTapHighlightColor: "transparent",
  textAlign: "left",
};
const tileDisabled = {
  ...tile,
  background: "#f8fafc",
  cursor: "not-allowed",
  opacity: 0.55,
  borderColor: "#e2e8f0",
};

const tileName = {
  fontSize: "12px",
  fontWeight: 700,
  color: "#1e293b",
  flex: 1,
  lineHeight: 1.3,
};
const tileNameDisabled = {
  ...tileName,
  color: "#94a3b8",
};

export default Bet;
